/**
 * The reset screen — both halves of it, on the sign-in screen's own layout.
 *
 * ── why one component and not two ───────────────────────────────────────────
 *
 * A person is in exactly one of two states, and which one is decided by the URL,
 * not by anything they clicked: either they are asking for a link, or they have
 * followed one. Splitting that into two exported screens would push the decision
 * into every host app, and there are three of them (portal, console, extension).
 * One component owns the decision; the host maps branding once, the same way it
 * maps it onto `SignIn`.
 *
 * ── every identity fact is still a prop (#125) ──────────────────────────────
 *
 * Same contract as `SignIn`: layout, states and accessibility live here; the
 * logo, headline, legal links and footnote arrive as data from
 * `portal.branding()`. A customer's consumer resetting their password must see
 * THEIR portal, and the mail they were sent carries the same identity — that is
 * why `portal.password_reset_delivery` returns `portal.mail_identity` rather
 * than letting `core.render_template` fall back to the deployment brand.
 *
 * ── the three things this screen must not do ────────────────────────────────
 *
 * 1. **It must not say whether the address exists.** The confirmation is
 *    identical whatever `onRequest` resolves to, and `onRequest`'s own contract
 *    is that it resolves for a local account, a federated one and an unknown
 *    address alike. Even a REJECTION is rendered as the same confirmation, so a
 *    network failure cannot become an oracle either — the honest cost of that is
 *    written on the screen ("if you do not get it, try again").
 * 2. **It must not leave the person signed in.** Completing a reset calls
 *    `setPasswordWithRecoveryGrant`, which revokes every session including the
 *    recovery grant, and this screen then sends them to sign in. Clicking a link
 *    in an email is not a login.
 * 3. **It must not keep the grant.** The hash or legacy token is read out of
 *    the URL fragment once and stripped in the same call. A new hash is not
 *    exchanged until the person presses Continue; the resulting grant lives
 *    only in a ref and is never written to storage.
 *
 * ── the client is the provider's, exactly as SignIn's ───────────────────────
 *
 * Exchange and password change go through `useInjectedAuth().client`, never
 * the environment singleton in `react.tsx`. A first-party host's `AuthProvider`
 * injects that singleton, so the Console and portal are unchanged; a solution
 * host (`@solum/browser-client`) injects the canonical client it configured,
 * so the same screen spends the grant against ITS GoTrue and nothing reads
 * `VITE_*` build variables.
 */
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { BrandLogo, Button, Field, Input, useOptionalLocalization } from '@solum/ui'
import { useInjectedAuth } from './provider.js'
import { safeLink, type SignInLink } from './SignIn.js'
import {
  readRecoveryFromHash,
  type RecoveryChallenge,
  type RecoveryGrant,
  type RecoveryError,
} from './recovery.js'
import { MIN_LENGTH, passwordIssues, passwordScore } from './password.js'
import s from './SignIn.module.css'

export interface ResetPasswordProps {
  /**
   * Ask the platform for a reset. The host wires this to
   * `portal.request_password_reset`, which returns a constant for every outcome
   * — so this callback resolving means "the request was accepted", never "the
   * account exists".
   */
  onRequest: (email: string) => Promise<void>
  /**
   * Record the completed reset as a governed act naming the person
   * (`portal.complete_password_reset`). Optional: a host with no portal module
   * still gets a working reset, it simply leaves no act of ours.
   *
   * Called BEFORE the sessions are revoked, because it needs the recovery
   * session to authenticate — and it must not be able to fail the reset, so its
   * rejection is swallowed. The password change is the thing that matters and it
   * has already happened by then.
   */
  onCompleted?: () => Promise<void>
  /** Leave the reset screen — back to sign-in. */
  onDone: () => void
  /** Prefill, when the host knows who was trying to sign in. */
  initialEmail?: string

  // ── the identity slots, identical to SignIn's ──
  title?: string
  logo?: string | null
  headline?: string | null
  tagline?: string | null
  assurance?: string | null
  legal?: SignInLink[] | null
  footnote?: string | null
  /** Rendered under the form, as SignIn's is. */
  footer?: ReactNode
}

/**
 * One sentence, used for a local account, a federated one, an unknown address
 * and a throttled caller alike. It promises nothing it cannot keep — note it
 * does not say "we have sent you an email", because for a federated identity
 * what arrives is not a reset link, and for an unknown address (by default)
 * nothing arrives at all.
 */
/**
 * The compact card's lockup: the logo when there is one, the governed NAME when
 * there is not (#2296).
 *
 * Below 900px the aside is not rendered, and every card here drew
 * `{logo && <BrandLogo/>}` and nothing else — so a deployment that declares a
 * name and no mark landed on an unbranded recovery screen, which is precisely
 * what a branded recovery mail must never do. Measured in a browser at 390x844;
 * `SignIn` had the same gap and the same fix.
 *
 * The headline is the governed public name — the anonymous brand projection
 * admits no free text — so this is identity rather than decoration.
 */
export function CardLockup({ logo, headline }: { logo?: string | null; headline?: string | null }) {
  if (logo) return <BrandLogo logo={logo} className={s.cardLogo} testId="reset-logo-compact" />
  const name = (headline ?? '').split(String.fromCharCode(10)).map((l) => l.trim()).filter(Boolean)[0]
  return name ? <p className={s.cardBrand} data-testid="reset-brand-compact">{name}</p> : null
}

const CONFIRMATION =
  'If that address can be used to sign in here, you will get an email with what to do next. It can take a minute to arrive — check your spam folder before trying again.'

function useAuthMessage() {
  const localization = useOptionalLocalization()
  return (key: string, fallback: string, variables?: Readonly<Record<string, unknown>>) =>
    localization?.message(key, fallback, variables) ?? fallback
}

export function ResetPassword(props: ResetPasswordProps) {
  const {
    onRequest, onCompleted, onDone, initialEmail,
    title, logo, headline, tagline, assurance, legal, footnote, footer,
  } = props

  /**
   * Read the fragment ONCE, synchronously, on the first render — not in an
   * effect. An effect runs after the browser has already painted and, in
   * StrictMode, twice; the grant would sit in the address bar (and in history)
   * for that whole window. `useState`'s initialiser runs before anything is
   * committed and exactly once. Reading only captures the opaque hash; the
   * explicit Continue action below is the first operation that can spend it.
   */
  const [initial] = useState(() => readRecoveryFromHash())
  const grant = useRef<RecoveryGrant | null>(initial.grant)
  const [challenge, setChallenge] = useState<RecoveryChallenge | null>(initial.challenge)
  const [linkError, setLinkError] = useState<RecoveryError | null>(initial.error)

  const phase = grant.current ? 'set' : challenge ? 'continue' : 'request'

  return (
    <AuthScreenFrame logo={logo} headline={headline} tagline={tagline} assurance={assurance}
      legal={legal} footnote={footnote}>
      {phase === 'set'
        ? <SetNewPassword grant={grant} title={title} logo={logo} headline={headline}
            onCompleted={onCompleted} onDone={onDone} footer={footer} />
        : phase === 'continue'
          ? <ContinueRecovery challenge={challenge!} title={title} logo={logo} headline={headline}
              onReady={(next) => { grant.current = next; setChallenge(null) }}
              onInvalid={(error) => { setLinkError(error); setChallenge(null) }}
              onDone={onDone} footer={footer} />
          : <RequestLink initialEmail={initialEmail} linkError={linkError}
              title={title} logo={logo} headline={headline} onRequest={onRequest} onDone={onDone} footer={footer} />}
    </AuthScreenFrame>
  )
}

/**
 * The page both auth-mail screens share (#2689): the sign-in layout's aside
 * and form side, with the legal footer. Extracted from `ResetPassword` so the
 * scanner-safe mail landing draws the SAME screen, not a copy of it.
 */
export function AuthScreenFrame({
  logo, headline, tagline, assurance, legal, footnote, children,
}: {
  logo?: string | null
  headline?: string | null
  tagline?: string | null
  assurance?: string | null
  legal?: SignInLink[] | null
  footnote?: string | null
  children: ReactNode
}) {
  return (
    <div className={s.page} data-aside={Boolean(headline || tagline || assurance || logo) || undefined}>
      {(headline || tagline || assurance || logo) && (
        <aside className={s.aside} data-testid="reset-aside">
          <span className={s.glowBrand} aria-hidden="true" />
          <span className={s.glowAccent} aria-hidden="true" />
          <div className={s.asideInner}>
            {logo && <BrandLogo logo={logo} className={s.asideLogo} testId="reset-logo" />}
            {headline && (
              <p className={s.headline}>
                {headline.split('\n').map((l) => l.trim()).filter(Boolean).map((line, i, all) => (
                  <span key={`${i}-${line}`} className={i === all.length - 1 ? s.headlineAccent : undefined}>
                    {line}{i < all.length - 1 && <br />}
                  </span>
                ))}
              </p>
            )}
            {tagline && <p className={s.tagline}>{tagline}</p>}
            {assurance && <p className={s.assurance}>{assurance}</p>}
          </div>
        </aside>
      )}

      <div className={s.formSide}>
        {children}

        <LegalFooter legal={legal} footnote={footnote} />
      </div>
    </div>
  )
}

function ContinueRecovery({
  challenge, title, logo, headline, onReady, onInvalid, onDone, footer,
}: {
  challenge: RecoveryChallenge
  title?: string
  logo?: string | null
  headline?: string | null
  onReady: (grant: RecoveryGrant) => void
  onInvalid: (error: RecoveryError) => void
  onDone: () => void
  footer?: ReactNode
}) {
  const message = useAuthMessage()
  const { client } = useInjectedAuth()
  const [busy, setBusy] = useState(false)

  async function continueReset() {
    setBusy(true)
    try {
      onReady(await client.exchangeRecoveryTokenHash(challenge.tokenHash))
    } catch (error) {
      onInvalid({
        code: 'recovery_exchange_failed',
        description: error instanceof Error
          ? error.message
          : message('auth.reset.error.link_invalid', 'That reset link is no longer valid.'),
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={s.card}>
      <CardLockup logo={logo} headline={headline} />
      <h1 className={s.title}>{title ?? message('auth.reset.title', 'Reset your password')}</h1>
      <p className={s.sub}>
        {message('auth.reset.continue.detail', 'Confirm that you opened this message before the one-time reset is used.')}
      </p>
      <Button type="button" variant="primary" loading={busy} disabled={busy}
        onClick={() => { void continueReset() }}>
        {message('auth.reset.action.continue', 'Continue password reset')}
      </Button>
      <Button type="button" variant="ghost" onClick={onDone}>
        {message('auth.action.cancel', 'Cancel')}
      </Button>
      {footer}
    </div>
  )
}

function LegalFooter({ legal, footnote }: { legal?: SignInLink[] | null; footnote?: string | null }) {
  const message = useAuthMessage()
  const links = (legal ?? [])
    .map((l) => ({ label: l.label, href: safeLink(l.href) }))
    .filter((l): l is SignInLink => Boolean(l.href && l.label))
  if (links.length === 0 && !footnote) return null
  return (
    <div className={s.legal}>
      {links.length > 0 && (
        <nav className={s.legalLinks} aria-label={message('auth.legal.label', 'Legal')}>
          {links.map((l) => (
            <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer">{l.label}</a>
          ))}
        </nav>
      )}
      {footnote && <p className={s.footnote}>{footnote}</p>}
    </div>
  )
}

// ── phase 1 · ask for a link ────────────────────────────────────────────────

function RequestLink({
  initialEmail, linkError, title, logo, headline, onRequest, onDone, footer,
}: {
  initialEmail?: string
  linkError: RecoveryError | null
  title?: string
  logo?: string | null
  headline?: string | null
  onRequest: (email: string) => Promise<void>
  onDone: () => void
  footer?: ReactNode
}) {
  const message = useAuthMessage()
  const [email, setEmail] = useState(initialEmail ?? '')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const errorId = useId()

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    // Validated in the form rather than by disabling the button — the same
    // reasoning SignIn records: a disabled submit announces nothing, and a
    // screen-reader user gets a control that is simply not there.
    if (!email.trim() || !email.includes('@')) {
      setError(message('auth.reset.request.email_required', 'Enter the email address you sign in with.'))
      return
    }
    setBusy(true)
    try {
      await onRequest(email.trim())
    } catch {
      // DELIBERATELY swallowed. A failure that renders differently from a
      // success is an oracle: an attacker who can make the request fail for
      // addresses that exist (or observe that it only fails for ones that do
      // not) gets exactly what the constant response was written to withhold.
      // The screen says the same thing, and says plainly that it might not have
      // worked.
    } finally {
      setBusy(false)
      setSent(true)
    }
  }

  if (sent) {
    return (
      <div className={s.card}>
        <CardLockup logo={logo} headline={headline} />
        <h1 className={s.title}>{message('auth.confirmation.check_email', 'Check your email')}</h1>
        {/* role=status, not alert: this is the expected outcome, and an
            assertive announcement would interrupt whatever the person is
            reading. */}
        <p className={s.sub} role="status" data-testid="reset-confirmation">
          {message('auth.reset.sent.detail', CONFIRMATION)}
        </p>
        <Button type="button" variant="secondary" onClick={onDone}>
          {message('auth.action.back_to_sign_in', 'Back to sign in')}
        </Button>
      </div>
    )
  }

  return (
    <form className={s.card} onSubmit={submit} noValidate>
      <CardLockup logo={logo} headline={headline} />
      <h1 className={s.title}>{title ?? message('auth.reset.title', 'Reset your password')}</h1>
      <p className={s.sub}>
        {message('auth.reset.request.detail', 'Enter the address you sign in with and we will send you what you need.')}
      </p>

      {/* An expired or already-used link brings the person back here with
          GoTrue's own message. Showing it is the difference between "nothing
          happened" and "that link is spent, ask for another" — and GoTrue's
          wording is written for an end user, so it is shown rather than
          paraphrased into a guess. */}
      {linkError && (
        <div className={s.error} role="alert" data-testid="reset-link-error">
          {linkError.description} {message('auth.reset.request.link_error_suffix', 'Ask for a new one below.')}
        </div>
      )}
      {error && <div className={s.error} id={errorId} role="alert">{error}</div>}

      <Field label={message('auth.field.email', 'Email')} required>
        <Input
          type="email"
          value={email}
          autoComplete="username"
          autoFocus
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>

      <Button type="submit" variant="primary" loading={busy} disabled={busy}>
        {message('auth.reset.action.send', 'Send reset instructions')}
      </Button>
      <Button type="button" variant="ghost" onClick={onDone}>
        {message('auth.action.back_to_sign_in', 'Back to sign in')}
      </Button>
      {footer}
    </form>
  )
}

// ── phase 2 · choose a new password ─────────────────────────────────────────

export function SetNewPassword({
  grant, title, logo, headline, onCompleted, onDone, footer, purpose = 'reset',
}: {
  grant: React.RefObject<RecoveryGrant | null>
  /** `invitation` (#2689): the invitee's first password, through the same
   *  write + global revoke + local clear, with words that do not say "reset". */
  purpose?: 'reset' | 'invitation'
  title?: string
  logo?: string | null
  headline?: string | null
  onCompleted?: () => Promise<void>
  onDone: () => void
  footer?: ReactNode
}) {
  const message = useAuthMessage()
  const { client } = useInjectedAuth()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const errorId = useId()
  const email = grant.current?.email ?? null

  // The policy runs as they type so nothing is a surprise at submit — but it is
  // never used to DISABLE the button (see SignIn's note), only to tell them.
  const issues = password ? passwordIssues(password, { email }) : []
  const score = passwordScore(password, { email })

  useEffect(() => {
    // Belt and braces on top of `readRecoveryFromHash`, which already stripped
    // the fragment. If anything ever re-adds one — a bfcache restore, a host app
    // that re-renders on a hashchange — it goes again.
    const clear = () => {
      if (typeof window !== 'undefined' && window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
      }
    }
    window.addEventListener('hashchange', clear)
    return () => { window.removeEventListener('hashchange', clear) }
  }, [])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const problems = passwordIssues(password, { email })
    if (problems.length > 0) { setError(problems[0]!.message); return }
    if (password !== confirm) {
      setError(message('auth.reset.error.changed_mismatch', 'Those two passwords are not the same.'))
      return
    }

    const token = grant.current?.accessToken
    if (!token) {
      setError(purpose === 'reset'
        ? message('auth.reset.error.expired', 'That reset link is no longer valid. Ask for a new one.')
        : message('auth.invitation.error.expired', 'That invitation link is no longer valid. Ask for a new invitation.'))
      return
    }

    setBusy(true)
    try {
      await client.setPasswordWithRecoveryGrant(token, password, purpose)
      // The grant is spent. Drop the reference so a second submit — a
      // double-click, a Back — cannot replay it.
      grant.current = null
      // The governed act, on OUR chain. After the change and before the person
      // leaves, because it needs the session that the revoke has just ended…
      // which is why it is attempted here and its failure cannot undo anything:
      // the password IS changed, and surfacing an error now would tell the
      // person their reset failed when it did not.
      if (onCompleted) { try { await onCompleted() } catch { /* recorded on the row as incomplete */ } }
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : message('auth.reset.error.change_failed', 'Could not set your password.'))
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className={s.card}>
        <CardLockup logo={logo} headline={headline} />
        <h1 className={s.title}>{purpose === 'reset'
          ? message('auth.reset.changed.title', 'Password changed')
          : message('auth.invitation.password_set.title', 'Password set')}</h1>
        <p className={s.sub} role="status" data-testid="reset-done">
          {purpose === 'reset'
            ? message('auth.reset.changed.detail', 'Everywhere you were signed in has been signed out, including here. Sign in with your new password to continue.')
            : message('auth.invitation.password_set.detail', 'Your password is set. Sign in with it to continue and accept your invitation.')}
        </p>
        <Button type="button" variant="primary" onClick={onDone}>
          {message('auth.action.sign_in', 'Sign in')}
        </Button>
      </div>
    )
  }

  return (
    <form className={s.card} onSubmit={submit} noValidate>
      <CardLockup logo={logo} headline={headline} />
      <h1 className={s.title}>{title ?? (purpose === 'reset'
        ? message('auth.reset.choose.title', 'Choose a new password')
        : message('auth.invitation.choose_password.title', 'Choose your password'))}</h1>
      <p className={s.sub}>
        {email
          ? message('auth.reset.choose.detail_named', `For ${email}. At least ${MIN_LENGTH} characters. A few ordinary words in a row beats a short one with symbols in it.`, { email, minimum: MIN_LENGTH })
          : message('auth.reset.choose.detail', `At least ${MIN_LENGTH} characters. A few ordinary words in a row beats a short one with symbols in it.`, { minimum: MIN_LENGTH })}
      </p>

      {error && <div className={s.error} id={errorId} role="alert">{error}</div>}

      <Field label={message('auth.field.new_password', 'New password')} required>
        <Input
          type="password"
          value={password}
          autoComplete="new-password"
          autoFocus
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>

      {/* The meter is a real progress element rather than five coloured divs, so
          it is announced and it degrades honestly. Its text is what a screen
          reader gets; the bar is the sighted shorthand for the same thing. */}
      {password && (
        <div className={s.meter} data-testid="reset-strength">
          <progress max={4} value={score} aria-label={message('auth.password.strength', 'Password strength')} />
          <span>{issues[0]?.message ?? [
            '',
            message('auth.password.weak', 'Weak'),
            message('auth.password.fair', 'Fair'),
            message('auth.password.good', 'Good'),
            message('auth.password.strong', 'Strong'),
          ][score]}</span>
        </div>
      )}

      <Field label={message('auth.field.confirm_new_password', 'Confirm new password')} required>
        <Input
          type="password"
          value={confirm}
          autoComplete="new-password"
          onChange={(e) => setConfirm(e.target.value)}
        />
      </Field>

      <Button type="submit" variant="primary" loading={busy} disabled={busy}>
        {purpose === 'reset'
          ? message('auth.reset.action.set_password', 'Set new password')
          : message('auth.invitation.action.set_password', 'Set password')}
      </Button>
      <Button type="button" variant="ghost" onClick={onDone}>
        {message('auth.action.cancel', 'Cancel')}
      </Button>
      {footer}
    </form>
  )
}
